import Avatar from '@mui/material/Avatar'
import _ from 'lodash'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Placeholder from '../../../../../images/placeholder120x120.png'
import { getProductUrl } from '../../../urls'
import KitQuantityStepper from './KitQuantityStepper'
import VariantPrice from './VariantPrice'
import VariantsSelect from './VariantsSelect'
import { ISupplyDataProduct, ISupplyDataVariant } from '@components/Product/VariantKitPanel/VariantKitPanel'
import { MAX_PRODUCT_QTY } from '../../../../constants'
import CheckCircleIcon from '@icons/CheckCircleIcon'
import CircleIcon from '@icons/CircleIcon'

interface VariantPayload {
  supplyProductPk: number
  pk: number
  quantity: number
}

interface Props {
  supplyProduct: ISupplyDataProduct
  variants: ISupplyDataVariant[]

  moveTop(supplyProductPk: number): void

  moveToBottom(supplyProductPk: number): void

  removeVariant(supplyVariantPk: number): void

  addVariant(supplyVariantPk: number, data: VariantPayload): void

  onClosePanel(): void

  isWholesale: boolean
}

const VariantDetails: React.FC<Props> = ({
  supplyProduct,
  variants,
  moveTop,
  moveToBottom,
  removeVariant,
  addVariant,
  onClosePanel,
  isWholesale,
}) => {
  const initialVariant = variants.find(el => el.isInCart) || variants[0]
  const [selected, setSelected] = useState<ISupplyDataVariant>(initialVariant)
  const [quantity, setQuantity] = useState(
    initialVariant.isInCart
      ? initialVariant.cartQuantity
      : !initialVariant.cartLine &&
        (initialVariant.stockQty || ['in', 'backorder'].includes(initialVariant.outOfStockStatus))
      ? 1
      : 0
  )

  const createDataSelected = (variant: ISupplyDataVariant, quantity: number): VariantPayload => {
    return {
      supplyProductPk: variant.supplyProductPk,
      pk: variant.supplyVariantPk,
      quantity: quantity,
    }
  }

  const changeQuantity = (newQty: number) => {
    if (quantity > 0 && newQty <= 0) {
      moveToBottom(selected.supplyProductPk)
    } else if (quantity === 0 && newQty > 0) {
      moveTop(selected.supplyProductPk)
    }
    if (newQty <= 0) {
      removeVariant(selected.supplyVariantPk)
    } else {
      const data = createDataSelected(selected, newQty)
      addVariant(selected.supplyVariantPk, data)
    }
    setQuantity(newQty)
  }

  const onChange = e => {
    const prev = selected
    const val = e.target.value
    removeVariant(prev.supplyVariantPk)
    const variant = _.find(variants, o => String(o.supplyVariantPk) === val)
    if (variant) {
      setSelected(variant)
      if (variant.stockQty < quantity) {
        changeQuantity(variant.stockQty)
      }
      const newQty = variant.stockQty < quantity ? variant.stockQty : quantity
      if (newQty > 0) {
        const data = createDataSelected(variant, newQty)
        addVariant(selected.supplyVariantPk, data)
      }
    }
  }

  const isIncluded = quantity > 0
  const canInclude =
    !selected.isShippingRequired || selected.stockQty > 0 || ['in', 'backorder'].includes(selected.outOfStockStatus)

  const toggleIncluded = () => {
    if (isIncluded) {
      changeQuantity(0)
    } else if (canInclude) {
      changeQuantity(1)
    }
  }

  return (
    <div
      className={`w-full flex items-start gap-10 p-10 rounded-lg border border-solid border-gray-300 ${
        isIncluded ? 'bg-green-100' : 'bg-white'
      }`}
    >
      <span
        className={`flex-shrink-0 self-center flex ${
          canInclude || isIncluded ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
        }`}
        onClick={toggleIncluded}
      >
        {isIncluded ? (
          <CheckCircleIcon fontSize='large' className='text-secondary' viewBox='0 -960 960 960' />
        ) : (
          <CircleIcon fontSize='large' className='text-secondary' viewBox='0 -960 960 960' />
        )}
      </span>
      <span className={`flex-shrink-0 w-[55px] h-[70px] shadow-thumbnail`}>
        <Avatar
          alt={supplyProduct.specificsName}
          src={supplyProduct.imageURL || Placeholder}
          style={{ width: '100%', height: '100%' }}
          variant='rounded'
        />
      </span>
      <span className={`flex-1 min-w-0`}>
        <Link
          className='no-underline hover:underline'
          to={getProductUrl(supplyProduct.specificsSlug, supplyProduct.specificsPk)}
          onClick={onClosePanel}
        >
          <span className='font-bold text-base block line-clamp-2'>{supplyProduct.specificsName}</span>
        </Link>
        <VariantsSelect variants={variants} onChange={onChange} selected={selected.supplyVariantPk} />
      </span>
      <span className='flex-shrink-0 self-stretch flex flex-col items-end justify-between'>
        <VariantPrice
          price={Number(isWholesale ? selected.wholesalePrice.gross : selected.price.gross)}
          priceUndiscounted={Number(isWholesale ? selected.wholesalePrice.gross : selected.priceUndiscounted.gross)}
          currency={selected.priceUndiscounted.currency}
        />
        {selected.isShippingRequired && isIncluded && (
          <KitQuantityStepper
            quantity={quantity}
            availableQuantity={
              ['backorder', 'in'].includes(selected.outOfStockStatus) ? MAX_PRODUCT_QTY : selected.stockQty
            }
            onChangeQuantity={changeQuantity}
            canZero
          />
        )}
      </span>
    </div>
  )
}

export default VariantDetails
